import Navbar from "@/components/Navbar";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Users, UserPlus } from "lucide-react";
import DomainElements from "@/components/gsa/DomainElements";
import CircularDomainDial from "@/components/gsa/CircularDomainDial";
import { Domain } from "@/types/domain";
import KnowledgeNetworkStats from "@/components/gsa/KnowledgeNetworkStats";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
const domains: Domain[] = [{
  symbol: "Design Automation",
  name: "Design Automation",
  categories: "Requirements Generation, Design Tools, Subject Matter Expertise Customization"
}, {
  symbol: "Space &/or Furniture Layout",
  name: "Space &/or Furniture Layout",
  categories: "Workplace Modeling, Scenario Testing, Automated Space Planning, Generative Furniture Design"
}, {
  symbol: "Analysis Tools",
  name: "Analysis Tools",
  categories: "Zoning & Code Compliance, Space Utilization & Circulation Analysis, Daylighting Analysis, View Analysis, Acoustic Analysis, Automated Plan Checking, Clash Detection & Conflict Resolution, Predictive Issue Detection, Smart Building Management"
}, {
  symbol: "Cost and Schedule Estimation",
  name: "Cost and Schedule Estimation",
  categories: "Material & Cost Optimization, Procurement & Bid Support, Schedule Scenarios"
}, {
  symbol: "Visualization & Rendering",
  name: "Visualization & Rendering",
  categories: "3D Modeling & Visualization, Style Transfer & Concept Art"
}, {
  symbol: "Post-Occupancy Monitoring",
  name: "Post-Occupancy Monitoring",
  categories: "Employee Experience & Feedback Integration, Predictive Maintenance, Building System Monitoring, Occupant Comfort & Building Performance Feedback, Building Performance"
}];
const gsaDomains: Domain[] = [{
  symbol: "Federal Procurement and Contracting",
  name: "Federal Procurement and Contracting",
  categories: "Government-wide Acquisition Contracts (GWACs), Multiple Award Schedules (MAS), Simplified Acquisition Procedures (SAP), Federal Supply Schedules (FSS), Procurement Integrity and Compliance, Contractor Performance Evaluation"
}, {
  symbol: "Real Estate and Public Buildings",
  name: "Real Estate and Public Buildings",
  categories: "Federal Property Management, Lease Administration and Negotiation, Sustainable Building Standards, Public Building Lifecycle Management, Historic Property Preservation, Space Optimization and Utilization"
}, {
  symbol: "IT and Digital Transformation",
  name: "IT and Digital Transformation",
  categories: "Cloud Adoption for Federal Agencies, Government-wide Cybersecurity Frameworks, AI and Automation in Federal Operations, Data Governance and Privacy in GSA, IT Modernization Initiatives, Digital Services Procurement"
}, {
  symbol: "Fleet Management and Transportation Services",
  name: "Fleet Management and Transportation Services",
  categories: "Federal Vehicle Procurement, Electric and Hybrid Fleet Transition, GPS and Telematics for Government Vehicles, Federal Transportation Cost Optimization, Maintenance and Lifecycle Management, Public Sector Supply Chain Logistics"
}, {
  symbol: "Supply Chain and Logistics Management",
  name: "Supply Chain and Logistics Management",
  categories: "Federal Supply Chain Resilience, Warehouse and Inventory Management, Strategic Sourcing for Government Agencies, Global Supply Chain Risk Mitigation, Vendor Management and Acquisition, Logistics and Distribution Optimization"
}, {
  symbol: "Public-Private Partnerships (P3)",
  name: "Public-Private Partnerships (P3)",
  categories: "Federal Infrastructure Investment Models, P3 Contracting and Compliance, Risk-sharing Mechanisms in Federal Projects, Alternative Funding for Federal Programs, Performance-based Contracting"
}, {
  symbol: "Federal Workforce Management",
  name: "Federal Workforce Management",
  categories: "Human Capital Strategy in Federal Agencies, Federal Employee Training and Upskilling, DEI Initiatives and Compliance, Remote and Hybrid Work Policies, Leadership Development for Public Sector"
}, {
  symbol: "Energy and Sustainability Initiatives",
  name: "Energy and Sustainability Initiatives",
  categories: "Green Procurement Policies, Net Zero Energy in Federal Buildings, Energy Performance Contracting (EPC), Climate Resilience in Federal Infrastructure, Renewable Energy Adoption in GSA"
}, {
  symbol: "Federal Grants and Funding Programs",
  name: "Federal Grants and Funding Programs",
  categories: "Grant Application and Compliance, Federal Economic Stimulus Packages, Disaster Recovery and FEMA Grants, Cooperative Agreements and Cost-sharing, Grant Program Impact Assessments"
}, {
  symbol: "Emergency Preparedness and Continuity of Operations",
  name: "Emergency Preparedness and Continuity of Operations",
  categories: "Federal Disaster Response Planning, Government Resilience Frameworks, Emergency Procurement Procedures, Cybersecurity and Infrastructure Resilience, Continuity of Operations (COOP) Planning"
}, {
  symbol: "Federal Travel and Expense Management",
  name: "Federal Travel and Expense Management",
  categories: "Per Diem and Travel Reimbursement Guidelines, Federal Employee Travel Programs, Contracting for Travel Management Services, Travel Policy Compliance Audits, Government Conference and Event Planning"
}, {
  symbol: "GSA Smart Cities and Urban Innovation",
  name: "GSA Smart Cities and Urban Innovation",
  categories: "Federal Smart City Strategies, IoT Applications for Public Infrastructure, Smart Energy Grid for Federal Buildings, Digital Identity Management for Public Services, Public Transportation Modernization"
}, {
  symbol: "Public Sector Cybersecurity and Risk Management",
  name: "Public Sector Cybersecurity and Risk Management",
  categories: "Federal Data Protection Standards, Identity and Access Management (IAM), Continuous Monitoring and Threat Intelligence, Cloud Security for Federal Systems, Cyber Risk and Compliance for Government Vendors"
}, {
  symbol: "GSA Vendor and Industry Engagement",
  name: "GSA Vendor and Industry Engagement",
  categories: "Industry Days and Federal Market Research, Vendor Prequalification and Certification, Small Business Contracting Strategies, GSA Schedule Pricing Analysis, Innovation Procurement Models"
}, {
  symbol: "Government Data and Open Access Initiatives",
  name: "Government Data and Open Access Initiatives",
  categories: "Federal Data Sharing Agreements, Open Government Data Strategies, AI-driven Analytics for Public Services, Cloud-based Data Warehousing, GIS and Geospatial Intelligence for Public Use"
}];
const networkStats = {
  totalParticipants: 1247,
  activeContributors: 543,
  knowledgeBases: 842,
  totalSolutions: 326,
  departments: [{
    name: "Office of the Administrator",
    participants: 89
  }, {
    name: "Federal Acquisition Service",
    participants: 342
  }, {
    name: "Public Buildings Service",
    participants: 298
  }, {
    name: "Office of the Chief Information Officer",
    participants: 127
  }, {
    name: "Office of Government-wide Policy",
    participants: 85
  }, {
    name: "Office of the General Counsel Overview",
    participants: 42
  }, {
    name: "Office of Strategic Communication",
    participants: 38
  }, {
    name: "Office of Congressional and Intergovernmental Affairs",
    participants: 35
  }, {
    name: "Office of Inspector General",
    participants: 33
  }, {
    name: "Office of Mission Assurance",
    participants: 31
  }, {
    name: "Office of the Chief Financial Officer",
    participants: 29
  }, {
    name: "Office of Administrative Services",
    participants: 27
  }, {
    name: "Office of Human Resources Management",
    participants: 25
  }, {
    name: "Office of Customer Experience",
    participants: 23
  }, {
    name: "Office of Small and Disadvantaged Business Utilization",
    participants: 23
  }]
};
const GsaChannel = () => {
  return <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-6">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <img src="/lovable-uploads/4d3923e2-7ccc-4fd3-bd00-e2f84a0c1d21.png" alt="US Federal Government Giblet" className="w-28 h-28 object-contain cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>GSA Channel</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <div className="flex-grow">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-2xl font-bold">Welcome to the U.S. National Knowledge Network</CardTitle>
                  <div className="flex items-center gap-4">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to="/gsa-call-for-solutions">
                            <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                              CfS
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Call for Solution</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Link to="/gsa-call-for-destruction">
                            <Button className="bg-black text-white hover:bg-[#1e603b] transition-colors">
                              CfD
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Call for Destruction</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">GSA Dashboard</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-2">
                  <KnowledgeNetworkStats stats={networkStats} />
                </div>
                <div className="flex flex-col gap-4">
                  <Card className="p-3 bg-blue-50">
                    <div className="flex items-center space-x-3 mb-2">
                      <Avatar className="h-12 w-12 border-2 border-blue-500">
                        <AvatarImage src="/lovable-uploads/5eb043bf-469d-46bf-abe8-2651aa4739b3.png" alt="Julia De Rosi" className="object-cover" />
                        <AvatarFallback className="bg-blue-100 text-blue-800 text-xl">JD</AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="text-lg font-semibold">GSA Network Ambassador</h3>
                        <Link to="/julia-derosi-profile" className="text-primary hover:text-primary/80 font-medium">
                          Julia De Rosi
                        </Link>
                        <p className="text-sm text-gray-600">Deputy Assistant Commissioner, Design and Construction</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      Leading the GSA knowledge network with 254 direct project managers and over 1,200 active participants across the organization.
                    </p>
                    <Button variant="outline" size="sm" className="w-full">
                      <UserPlus className="mr-2 h-4 w-4" />
                      Join Knowledge Network
                    </Button>
                  </Card>
                  
                  <a href="https://img1.wsimg.com/blobby/go/70e764c8-c764-4e83-ada3-5ccb3a393f3d/downloads/5add6269-a319-4fc0-9dcb-95a52a6c7b5a/iSPAI%20Industry_GSA.pdf?ver=1741886570881" target="_blank" rel="noopener noreferrer" className="transition-transform hover:scale-105 block">
                    <img src="/lovable-uploads/1055d4ec-0c8f-4663-81f4-ee6eed1dfc9c.png" alt="GSA Industry Template" className="rounded-lg shadow-lg w-full object-contain border border-gray-200" />
                  </a>
                  
                  {/* GSA PBS Domain Elements Section positioned under the Industry Template */}
                  <div className="mt-6">
                    <h2 className="text-xl font-semibold mb-4">GSA Public Buildings Service (PBS) Domain Elements</h2>
                    <CircularDomainDial domains={domains} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">GSA Domain Elements</h2>
              <DomainElements domains={gsaDomains} />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>;
};
export default GsaChannel;